import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { InboxTruck } from 'assets/Icons';
import moment from 'moment';
import QUERY_KEYS from 'network/config/queryKeys';
import { deleteInbox, markInboxAsRead } from 'network/services/inbox';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

type MessageListProp = {
  data: {
    id?: number;
    subject?: string;
    content?: string;
    dateTime?: string;
    read?: boolean;
    data?: {
      type: string;
      title: string;
      description: string;
      time: string;
    }[];
  };
  onDelete?: () => void;
  onMarkAllRead?: () => void;
};

const parseHtmlContent = (content: string) => {
  // Extract link text and URL from HTML content
  const linkMatch = content.match(/<a href="([^"]+)">([^<]+)<\/a>/);
  if (linkMatch) {
    const [fullMatch, url, text] = linkMatch;
    return {
      beforeLink: content.split(fullMatch)[0],
      linkUrl: url,
      linkText: text,
      afterLink: content.split(fullMatch)[1] || ''
    };
  }
  return {
    beforeLink: content,
    linkUrl: '',
    linkText: '',
    afterLink: ''
  };
};

export const MessageList = ({ data, onDelete, onMarkAllRead }: MessageListProp) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: markAsRead } = useMutation({
    mutationFn: () => markInboxAsRead(data.id!),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.INBOX]);
      if (!data.read) {
        enqueueSnackbar('Message marked as read', { variant: 'success' });
      }
    }
  });

  const { mutate: deleteMessage } = useMutation({
    mutationFn: () => deleteInbox(data.id!),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.INBOX]);
      enqueueSnackbar('Message deleted successfully', { variant: 'success' });
      onDelete?.();
      setShowDeleteConfirm(false);
    }
  });

  const parsedContent = data.content ? parseHtmlContent(data.content) : null;

  const handleClick = () => {
    if (!data.read) {
      markAsRead();
    }
    setIsExpanded(!isExpanded);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  return (
    <>
      <div
        className={`w-full border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 ${
          !data.read ? 'bg-blue-50' : ''
        }`}>
        <div className="p-3 sm:p-4">
          {/* Mobile Layout */}
          <div className="flex items-start gap-3">
            {/* Icon - Hidden on mobile, visible on larger screens */}
            <div className="hidden sm:flex flex-shrink-0 mt-1">
              <InboxTruck className="w-5 h-5 text-gray-500" />
            </div>

            {/* Content Container */}
            <div className="flex-1 min-w-0">
              {/* Subject Line */}
              {data.subject && (
                <h3
                  className="font-semibold text-gray-900 text-sm sm:text-base mb-1 cursor-pointer hover:text-blue-600"
                  onClick={handleClick}>
                  {data.subject}
                </h3>
              )}

              {/* Content Preview */}
              {parsedContent && (
                <div
                  className={`text-gray-600 text-xs sm:text-sm mb-2 sm:mb-1 ${
                    !isExpanded ? 'line-clamp-2 sm:line-clamp-1' : ''
                  }`}
                  onClick={handleClick}>
                  {parsedContent.beforeLink}
                  {parsedContent.linkUrl && (
                    <Link
                      to={parsedContent.linkUrl}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={e => e.stopPropagation()}>
                      {parsedContent.linkText}
                    </Link>
                  )}
                  {parsedContent.afterLink}
                </div>
              )}

              {/* Mobile: Show date below content */}
              <div className="sm:hidden flex justify-between items-center">
                <span className="text-xs text-gray-500">{moment(data.dateTime).format('MMM D, YYYY')}</span>
                <button onClick={handleDeleteClick} className="text-red-500 hover:text-red-700 text-xs">
                  Delete
                </button>
              </div>
            </div>

            {/* Desktop: Date and actions on the right */}
            <div className="hidden sm:flex flex-shrink-0 items-center gap-4">
              <span className="text-sm text-gray-500 whitespace-nowrap">
                {moment(data.dateTime).format('D MMMM YYYY')}
              </span>
              <button onClick={handleDeleteClick} className="text-red-500 hover:text-red-700 text-sm">
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="delete-dialog-title">
        <DialogTitle id="delete-dialog-title">Delete Message</DialogTitle>
        <DialogContent>Are you sure you want to delete this message? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => deleteMessage()} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
